import React, { memo, useCallback, useState } from 'react';
import PhoneNumberListHeader from './phone-number-list-header';
import AssignPhoneNumberModalContainer from '../assign-phone-number-modal';
import AssignPhoneNumberLabelModalContainer from '../assign-phone-number-label-modal';
import EnableCallRecordingModal from '../enable-call-recording-modal';
import EnableVoicemailGreetingModal from '../enable-voicemail-greeting-modal';
import SetCallForwardingRuleModal from '../set-call-forwarding-rule-modal';
import DialerSettingsModal from '../dialer-settings-modal';
import { IProps } from './types';
import { PhoneNumberListingFilters, PhoneNumberSettingsActionType } from '../../../../../../types/phone-numbers';

const PhoneNumberListHeaderContainer: React.FC<IProps> = (props) => {
  const { selectedPhoneNumbers, deSelectedPhoneNumbers, isSelectAllAcrossPages, phoneNumberFilters, search, refreshPhoneNumbers, handleClearSelection, showAssignModal, showAssignLabelModal, onAssignModalChange, onAssignLabelModalChange, onSettingsActionChange } = props;
  const [showEnableCallRecordingModal, setShowEnableCallRecordingModal] = useState(false);
  const [showEnableVoicemailGreetingModal, setShowEnableVoicemailGreetingModal] = useState(false);
  const [showSetCallForwardingRuleModal, setShowSetCallForwardingRuleModal] = useState(false);
  const [showDialerSettingsModal, setShowDialerSettingsModal] = useState(false);

  const convertFiltersToAPIFormat = useCallback((filters?: typeof phoneNumberFilters, searchText?: string): PhoneNumberListingFilters | undefined => {
    const apiFilters: PhoneNumberListingFilters = {};

    if (searchText && searchText.trim()) {
      apiFilters.searchText = searchText.trim();
    }

    if (filters) {
      if (filters.countryCodes.length > 0) {
        apiFilters.countryCodes = filters.countryCodes;
      }

      if (filters.regions.length > 0) {
        apiFilters.regions = filters.regions;
      }

      if (filters.teamMemberIds.length > 0) {
        apiFilters.teamMemberIds = filters.teamMemberIds;
      }

      if (filters.purchaseDate && filters.purchaseDate.length > 0) {
        apiFilters.purchaseDateStart = filters.purchaseDate[0]?.toISOString();
        apiFilters.purchaseDateEnd = filters.purchaseDate[1]?.toISOString();
      }
    }

    return Object.keys(apiFilters).length > 0 ? apiFilters : undefined;
  }, [phoneNumberFilters]);

  const handleAssignClick = useCallback(() => {
    onAssignModalChange?.(true);
  }, [onAssignModalChange]);

  const handleAssignModalClose = useCallback(() => {
    onAssignModalChange?.(false);
  }, [onAssignModalChange]);

  const handleAssignLabelClick = useCallback(() => {
    onAssignLabelModalChange?.(true);
  }, [onAssignLabelModalChange]);

  const handleAssignLabelModalClose = useCallback(() => {
    onAssignLabelModalChange?.(false);
  }, [onAssignLabelModalChange]);

  const handleEnableCallRecordingClick = useCallback(() => {
    onSettingsActionChange?.(PhoneNumberSettingsActionType.CALL_RECORDING);
    setShowEnableCallRecordingModal(true);
  }, [onSettingsActionChange]);

  const handleEnableCallRecordingModalClose = useCallback(() => {
    setShowEnableCallRecordingModal(false);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEnableCallRecordingSubmit = useCallback((_enabled: boolean) => {
    // TODO: Implement the actual call recording enable/disable logic
    // This will be implemented when the API endpoint is ready
    // _enabled indicates if call recording should be enabled or disabled
    setShowEnableCallRecordingModal(false);
  }, []);

  const handleEnableVoicemailGreetingClick = useCallback(() => {
    onSettingsActionChange?.(PhoneNumberSettingsActionType.VOICEMAIL);
    setShowEnableVoicemailGreetingModal(true);
  }, [onSettingsActionChange]);

  const handleEnableVoicemailGreetingModalClose = useCallback(() => {
    setShowEnableVoicemailGreetingModal(false);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEnableVoicemailGreetingSubmit = useCallback((_enabled: boolean) => {
    // TODO: Implement the actual voicemail greeting enable/disable logic
    // This will be implemented when the API endpoint is ready
    // _enabled indicates if voicemail greeting should be enabled or disabled
    setShowEnableVoicemailGreetingModal(false);
  }, []);

  const handleSetCallForwardingRuleClick = useCallback(() => {
    onSettingsActionChange?.(PhoneNumberSettingsActionType.CALL_FORWARDING);
    setShowSetCallForwardingRuleModal(true);
  }, [onSettingsActionChange]);

  const handleSetCallForwardingRuleModalClose = useCallback(() => {
    setShowSetCallForwardingRuleModal(false);
  }, []);


  const handleDialerSettingsClick = useCallback(() => {
    setShowDialerSettingsModal(true);
  }, []);

  const handleDialerSettingsModalClose = useCallback(() => {
    setShowDialerSettingsModal(false);
  }, []);

  

  return (
    <>
      <PhoneNumberListHeader
        {...props}
        onAssignClick={handleAssignClick}
        onAssignLabelClick={handleAssignLabelClick}
        onEnableCallRecordingClick={handleEnableCallRecordingClick}
        onEnableVoicemailGreetingClick={handleEnableVoicemailGreetingClick}
        onSetCallForwardingRuleClick={handleSetCallForwardingRuleClick}
        onDialerSettingsClick={handleDialerSettingsClick}
      />
      {showAssignModal && (
      <AssignPhoneNumberModalContainer
        show={showAssignModal}
        onClose={handleAssignModalClose}
        selectedPhoneNumbers={selectedPhoneNumbers}
        deSelectedPhoneNumbers={deSelectedPhoneNumbers}
        isSelectAllAcrossPages={isSelectAllAcrossPages}
        phoneNumberFilters={phoneNumberFilters}
        searchText={search}
        refreshPhoneNumbers={refreshPhoneNumbers}
        handleClearSelection={handleClearSelection}
        convertFiltersToAPIFormat={convertFiltersToAPIFormat}
      />
      )}
      {showAssignLabelModal && (
      <AssignPhoneNumberLabelModalContainer
        show={showAssignLabelModal}
        onClose={handleAssignLabelModalClose}
        selectedPhoneNumbers={selectedPhoneNumbers}
        deSelectedPhoneNumbers={deSelectedPhoneNumbers}
        isSelectAllAcrossPages={isSelectAllAcrossPages}
        phoneNumberFilters={phoneNumberFilters}
        searchText={search}
        refreshPhoneNumbers={refreshPhoneNumbers}
        handleClearSelection={handleClearSelection}
        convertFiltersToAPIFormat={convertFiltersToAPIFormat}
      />
      )}
      {showEnableCallRecordingModal && (
        <EnableCallRecordingModal
          show={showEnableCallRecordingModal}
          onClose={handleEnableCallRecordingModalClose}
          selectedPhoneNumbers={selectedPhoneNumbers}
          deSelectedPhoneNumbers={deSelectedPhoneNumbers}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          phoneNumberFilters={phoneNumberFilters}
          searchText={search}
          refreshPhoneNumbers={refreshPhoneNumbers}
          handleClearSelection={handleClearSelection}
          convertFiltersToAPIFormat={convertFiltersToAPIFormat}
        />
      )}
      {showEnableVoicemailGreetingModal && (
        <EnableVoicemailGreetingModal
          show={showEnableVoicemailGreetingModal}
          onClose={handleEnableVoicemailGreetingModalClose}
          selectedPhoneNumbers={selectedPhoneNumbers}
          deSelectedPhoneNumbers={deSelectedPhoneNumbers}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          phoneNumberFilters={phoneNumberFilters}
          searchText={search}
          refreshPhoneNumbers={refreshPhoneNumbers}
          handleClearSelection={handleClearSelection}
          convertFiltersToAPIFormat={convertFiltersToAPIFormat}
        />
      )}
      {showSetCallForwardingRuleModal && (
        <SetCallForwardingRuleModal
          show={showSetCallForwardingRuleModal}
          onClose={handleSetCallForwardingRuleModalClose}
          selectedPhoneNumbers={selectedPhoneNumbers}
          deSelectedPhoneNumbers={deSelectedPhoneNumbers}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          phoneNumberFilters={phoneNumberFilters}
          searchText={search}
          refreshPhoneNumbers={refreshPhoneNumbers}
          handleClearSelection={handleClearSelection}
          convertFiltersToAPIFormat={convertFiltersToAPIFormat}
        />
      )}
      {showDialerSettingsModal && (
        <DialerSettingsModal
          show={showDialerSettingsModal}
          onClose={handleDialerSettingsModalClose}
        />
      )}
    </>
  );
};

export default memo(PhoneNumberListHeaderContainer);

